import React, { useEffect } from "react";
import { useStore } from "../../store/store";
import { useRequiredMsgs } from "../../store/requiredMsgs";

interface UpdateObj {
  sillFinish?: string;
  sillStyle?: string;
}

function useSillSelector() {
  const addToQuote = useStore((state) => state.addToQuote);

  const doorType = useStore((state) => state.quote.doorType);

  const isCombo = doorType?.includes("Combo");

  const sillColorChoice = useStore((state) => state.quote.sillColor);
  const sillStyle = useStore((state) => state.quote.sillStyle);

  const isComboSillDifferent = useStore(
    (state) => state.quote.isComboSillDifferent,
  );
  const comboWindowSillStyle = useStore(
    (state) => state.quote.comboWindowSillStyle,
  );

  const direction = useStore((state) => state.quote.swingDirection);
  const screenType = useStore((state) => state.quote.screenType);
  const screenVal = useStore((state) => state.quote.addScreen);

  const paintPrimeChoice = useStore((state) => state.quote.paintAndPrime);
  const primeChoice = useStore((state) => state.quote.prime);
  const stainChoice = useStore((state) => state.quote.stain);
  const customPaintChoice = useStore((state) => state.quote.customPaintOrStain);

  const findRequired = useRequiredMsgs((state) => state.findRequired);
  const removeRequired = useRequiredMsgs((state) => state.removeRequired);

  const showRaised =
    (sillStyle === "Raised Sill" ||
      sillStyle === "Squared Dam" ||
      sillStyle === "Rounded Dam") &&
    doorType !== "Swing";

  const showRaisedComboWindow =
    comboWindowSillStyle === "Raised Sill" ||
    comboWindowSillStyle === "Squared Dam" ||
    comboWindowSillStyle === "Rounded Dam";

  const showSillFinish =
    !sillStyle?.includes("Ultra") &&
    (paintPrimeChoice || primeChoice || stainChoice || customPaintChoice)
      ? true
      : false;

  const isCentor = screenType?.includes("Centor");

  const handleCheck = (
    e: React.ChangeEvent<{ checked: boolean }>,
    name: string,
  ) => {
    const { checked } = e.target;

    addToQuote({
      isComboSillDifferent: name === "no" && checked ? true : false,
      comboWindowSillStyle: "",
    });
  };

  useEffect(() => {
    if (!screenVal || doorType === "Swing") return;
    screenVal &&
      !isCentor &&
      (sillStyle === "Raised Sill" || sillStyle === "Rounded Dam") &&
      addToQuote({ sillStyle: "Squared Dam" });
  }, [screenVal, sillStyle]);

  const removeSillRequireds = (type: string, label: string) => {
    switch (type) {
      case "sillStyle":
        label !== "Raised Sill" &&
          removeRequired("Sill Type", "select either Rounded or Squared Sill");
        break;
      case "sillColor":
        removeRequired("Sill Finish");
        break;
      default:
        return;
    }
  };

  const handleClick = (label: string, type: string) => {
    if (!type) return;

    const checked =
      type === "sillColor"
        ? label === sillColorChoice
        : type === "sillStyle"
          ? label === sillStyle
          : "";

    if (checked) {
      addToQuote({ [type]: "" }, `${type}`);
    } else {
      if (type === "sillStyle") {
        let updateObj: UpdateObj = {};

        if (label.includes("Ultra")) updateObj["sillFinish"] = "";

        if (screenVal && !isCentor && label === "Raised Sill") {
          updateObj["sillStyle"] = "Squared Dam";
          if (findRequired("Sill Type"))
            removeRequired(
              "Sill Type",
              "select either Rounded or Squared Sill",
            );
        } else {
          updateObj["sillStyle"] = label;
        }

        if (isCombo && label === comboWindowSillStyle) {
          updateObj["comboWindowSillStyle"] = "";
        }

        addToQuote(updateObj);
      } else {
        addToQuote({ sillColor: label });
      }

      if (
        findRequired("Sill Type") ||
        findRequired("Sill Finish") ||
        findRequired("select either Rounded or Squared Sill")
      ) {
        removeSillRequireds(type, label);
      }
    }
  };

  const handleClickCombo = (label: string) => {
    const checked = label === comboWindowSillStyle;

    if (checked) {
      addToQuote({ comboWindowSillStyle: "" }, "comboWindowSillStyle");
    } else {
      let updateObj: UpdateObj = {};

      if (screenVal && label === "Raised Sill") {
        updateObj["comboWindowSillStyle"] = "Squared Dam";
      } else {
        updateObj["comboWindowSillStyle"] = label;
      }

      addToQuote(updateObj);

      if (findRequired("Window Sill Type")) {
        removeRequired("Window Sill Type");
      }

      if (findRequired("select either Rounded or Squared Window Sill")) {
        removeRequired("select either Rounded or Squared Window Sill");
      }
    }
  };

  return {
    handleClick,
    showSillFinish,
    showRaised,
    direction,
    sillStyle,
    sillColorChoice,
    doorType,
    screenVal,
    isCentor,
    isComboSillDifferent,
    comboWindowSillStyle,
    showRaisedComboWindow,
    handleCheck,
    handleClickCombo,
  };
}

export default useSillSelector;
